/**
 * 나의 할일 필터·건수 — 순수 함수(배너·페이지·슬라이드 공용). 줄 순서는 서버가 정한 그대로 둔다(칸 → 현장 묶음).
 * 현장은 siteId 기준, 담당자는 응답 ownerMemberIds 기준. 담당자 필터 값 '' = 전체, 그 외 회원 ID 문자열.
 * 7일 일정 줄(입고·착공·공사완료)에는 담당자가 없어 현장 필터만 적용한다.
 * 담당자 선택 기억은 localStorage('td1Owner', 읽기·쓰기 실패 시 전체).
 */
import { useCallback, useState } from 'react';
import type { TodoItem, WeekDayView, WeekEntry } from './todoModel';

export const OWNER_ALL = '';
const OWNER_STORAGE_KEY = 'td1Owner';

/** 현장 필터 — siteId 기준. 선택 없으면 통과. */
export function siteOk(todoSiteId: number, selectedSiteId?: number): boolean {
  return selectedSiteId == null || todoSiteId === selectedSiteId;
}

const ownerOk = (t: TodoItem, selected: string) => !selected || t.ownerIds.includes(Number(selected));

export interface TodoFilter {
  owner: string;
  /** 현장 하나로 좁힐 때 siteId */
  siteId?: number;
  /** 이번 주 [조치 필요만] — 지금 처리할 일(빨강) 할일 줄만 남긴다(일정 줄 · 노랑 제외) */
  actionOnly?: boolean;
}

/** 조치가 필요한 줄 — 빨강 할일. 일정 줄(입고·착공·공사완료)은 조치 대상이 아니다. */
const needsAction = (w: WeekEntry) => w.kind === 'todo' && w.todo.level === 'r';

/** 담당자 선택이 이번 응답에 없는 사람이면(기억값이 낡음) 전체로 본다. */
export function resolveOwner(owner: string, todos: TodoItem[]): string {
  if (!owner) return OWNER_ALL;
  return todos.some((t) => ownerOk(t, owner)) ? owner : OWNER_ALL;
}

/** 오늘 탭 표시 줄 — 담당자·현장 필터 적용, 서버 순서 그대로 칸만 나눈다. */
export function visibleToday(items: TodoItem[], f: TodoFilter): { red: TodoItem[]; yellow: TodoItem[] } {
  const vis = items.filter((t) => ownerOk(t, f.owner) && siteOk(t.siteId, f.siteId));
  return {
    red: vis.filter((t) => t.level === 'r'),
    yellow: vis.filter((t) => t.level === 'y'),
  };
}

/** 오늘 건수 — 오늘 탭 표시 줄 수(담당자·현장 필터 적용). */
export function countToday(items: TodoItem[], f: TodoFilter): number {
  return items.filter((t) => ownerOk(t, f.owner) && siteOk(t.siteId, f.siteId)).length;
}

/** 7일 표시 — 현장 · [조치 필요만] 필터 후 줄 0건 날짜는 제외. 날짜 · 줄 순서는 서버 그대로. */
export function visibleWeek(days: WeekDayView[], f: TodoFilter): WeekDayView[] {
  return days
    .map((d) => ({ ...d, items: d.items.filter((w) => siteOk(w.siteId, f.siteId) && (!f.actionOnly || needsAction(w))) }))
    .filter((d) => d.items.length > 0);
}

/** 7일 현장별 보기 — 현장 카드 하나에 그 현장의 줄을 날짜 순으로. 현장 순서 = 그 현장이 처음 나온 날짜 순. */
export interface WeekSiteView {
  siteId: number;
  site: string;
  items: { entry: WeekEntry; dateLabel: string; today: boolean }[];
}

export function groupWeekBySite(days: WeekDayView[]): WeekSiteView[] {
  const bySite = new Map<number, WeekSiteView>();
  for (const d of days) {
    for (const entry of d.items) {
      let g = bySite.get(entry.siteId);
      if (!g) {
        g = { siteId: entry.siteId, site: entry.site, items: [] };
        bySite.set(entry.siteId, g);
      }
      g.items.push({ entry, dateLabel: d.label, today: d.today });
    }
  }
  return [...bySite.values()];
}

/** 7일 건수 — 표시 줄 수(오늘 할일 + 일정, 날짜 머리 건수와 같은 기준). */
export function countWeek(days: WeekDayView[], f: TodoFilter): number {
  return visibleWeek(days, f).reduce((n, d) => n + d.items.length, 0);
}

/** 응답에 등장하는 담당자(회원 ID) — 이름순(이름 모르면 뒤로, ID순). */
export function ownersOf(items: TodoItem[], names: ReadonlyMap<number, string>): number[] {
  const ids = new Set<number>();
  for (const t of items) for (const id of t.ownerIds) ids.add(id);
  return [...ids].sort((a, b) => {
    const na = names.get(a);
    const nb = names.get(b);
    if (na && nb) return na.localeCompare(nb, 'ko');
    if (na) return -1;
    if (nb) return 1;
    return a - b;
  });
}

/** 담당자 필터 항목 오른쪽 숫자 — 전체('')와 담당자별(키 = 회원 ID 문자열). */
export function countByOwnerToday(items: TodoItem[], siteId?: number): Record<string, number> {
  const c: Record<string, number> = { [OWNER_ALL]: 0 };
  for (const t of items) {
    if (!siteOk(t.siteId, siteId)) continue;
    c[OWNER_ALL] += 1;
    for (const id of t.ownerIds) c[String(id)] = (c[String(id)] ?? 0) + 1;
  }
  return c;
}

/** 같은 현장이 이어지면 첫 줄에만 현장명을 표시한다. */
export function markSiteStart<T extends { site: string }>(items: T[], prevSite = ''): { item: T; first: boolean }[] {
  let prev = prevSite;
  return items.map((item) => {
    const first = item.site !== prev;
    prev = item.site;
    return { item, first };
  });
}

function readOwner(): string {
  try {
    const v = localStorage.getItem(OWNER_STORAGE_KEY) || OWNER_ALL;
    return /^\d+$/.test(v) ? v : OWNER_ALL; // 예전 기억값(이름 문자열)은 버린다
  } catch {
    return OWNER_ALL;
  }
}

/** 담당자 필터 선택 + 기억(다시 열어도 유지). 저장소가 막혀 있으면 기억 없이 동작한다. */
export function useTodoOwner(): [string, (owner: string) => void] {
  const [owner, setOwnerState] = useState<string>(readOwner);
  const setOwner = useCallback((next: string) => {
    setOwnerState(next);
    try {
      localStorage.setItem(OWNER_STORAGE_KEY, next);
    } catch {
      /* 저장소 차단 — 기억만 포기 */
    }
  }, []);
  return [owner, setOwner];
}
